'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import AddToWishlistButton from './AddToWishlistButton'
import ProductSwiper from './ProductSwiper'
import OptionPicker from '@/components/shoplist/OptionPicker'
import { useContextElement } from '@/context/Context'
import styles from './ProductCard.module.css'

const toNumber = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const pickSkuPrice = (sku, p) => {
  const v =
    sku?.Price ??
    sku?.SalePrice ??
    sku?.SellPrice ??
    sku?.UnitPrice ??
    sku?.FinalPrice ??
    sku?.DiscountPrice

  const n = toNumber(v)
  if (n !== null) return n

  const pn = toNumber(p?.Price)
  return pn !== null ? pn : 0
}

const pickSkuOldPrice = (sku, p) => {
  const v =
    sku?.SuggestPrice ??
    sku?.OriginPrice ??
    sku?.OriginalPrice ??
    sku?.ListPrice ??
    sku?.MarketPrice ??
    sku?.MSRP ??
    p?.SuggestPrice

  const n = toNumber(v)
  return n !== null ? n : null
}

const normalizeForCartWithSku = (p, skuObj, unitPriceFromPicker) => {
  const skuPrice =
    toNumber(unitPriceFromPicker) !== null
      ? Number(unitPriceFromPicker)
      : pickSkuPrice(skuObj, p)

  const skuOld = pickSkuOldPrice(skuObj, p)

  return {
    ProductID: p.ProductID,
    ProductName: p.ProductName,
    Price: skuPrice,
    SuggestPrice: skuOld ?? p.SuggestPrice,
    Cost: p.Cost,
    PriceFromProduct: p.Price,
    PictureData: p.PictureData,
    SkuID: skuObj?.SkuID ?? p.SkuID,
    SkuName: skuObj?.SkuName,
    InventoryQty: skuObj?.InventoryQty ?? p.InventoryQty,
    LimitedQty: skuObj?.LimitedQty,
    ProductOption: p.ProductOption ?? [],
    IsPreorder: p?.IsPreorder === true,
  }
}

const isProductOutOfStock = (p) => {
  return Number(p?.TotalInvQty ?? 0) <= 0
}

const priceNode = (price, old) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
    {old && Number(old) > Number(price) ? (
      <span style={{ textDecoration: 'line-through', color: '#999' }}>
        ${Number(old || 0).toLocaleString()}
      </span>
    ) : null}

    <span style={{ fontWeight: 700 }}>
      ${Number(price || 0).toLocaleString()}
    </span>
  </div>
)

export default function ProductCard({
  i,
  elm,
  storeInfo,
  wishType,
  mdLength,
  lgLength,
  fontSize,
  priceFontSize,
  divObj,
}) {
  const { addProductToCart, cartProducts } = useContextElement()

  const [addingId, setAddingId] = useState(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerProduct, setPickerProduct] = useState(null)
  const [pickerSkuObj, setPickerSkuObj] = useState(null)
  const [pickerSkuPrice, setPickerSkuPrice] = useState(null)
  const [pickerSkuOldPrice, setPickerSkuOldPrice] = useState(null)

  const [toast, setToast] = useState({
    show: false,
    msg: '',
    type: 'success',
  })

  const hasDiscount =
    elm?.SuggestPrice &&
    elm?.Price &&
    Number(elm.Price) < Number(elm.SuggestPrice)

  const toastTimerRef = useRef(null)

  const productUrl = `/product-list/${elm.ProductID}`

  const wishlistButtonPosition = storeInfo?.WishlistButtonPosition || 'Image'

  const cartButtonPosition = storeInfo?.CartButtonPosition || 'Image'

  // 圖片的類型
  const imageAspectType = storeInfo?.ImageAspectType ?? '2'

  // 預購商品與售完商品的判斷
  const isPreorder = elm?.IsPreorder === true
  const outOfStock = !isPreorder && isProductOutOfStock(elm)
  const canAddToCart = isPreorder || !outOfStock

  const showToast = (msg, type = 'success', duration = 2200) => {
    setToast({ show: true, msg, type })

    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)

    toastTimerRef.current = setTimeout(() => {
      setToast((prev) => ({
        ...prev,
        show: false,
      }))
    }, duration)
  }

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!pickerOpen) return

    const prev = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'

    const onKey = (e) => {
      if (e.key === 'Escape') setPickerOpen(false)
    }

    window.addEventListener('keydown', onKey)

    return () => {
      document.documentElement.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [pickerOpen])

  const fetchProductDetail = async (productId) => {
    const params = new URLSearchParams({
      StoreNumber: process.env.NEXT_PUBLIC_STORE_NUMBER ?? '',
      ProductID: String(productId),
    })

    const res = await fetch(
      `/api/product/get_ProductDetail?${params.toString()}`,
      {
        cache: 'no-store',
      }
    )

    if (!res.ok) {
      throw new Error(`get_ProductDetail failed: ${res.status}`)
    }

    const json = await res.json()
    return json?.Data || json?.data || json
  }

  const fetchCartListOnce = async () => {
    try {
      const res = await fetch('/api/product/get_Cart_Product', {
        method: 'GET',
      })

      await res.json().catch(() => ({}))
    } catch {}
  }

  const handleAddClick = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!elm?.ProductID || addingId) return

    if (!isPreorder && outOfStock) {
      showToast('此商品補貨中', 'error')
      return
    }

    try {
      setAddingId(elm.ProductID)

      setPickerSkuObj(null)
      setPickerSkuPrice(null)
      setPickerSkuOldPrice(null)

      const detailProduct = await fetchProductDetail(elm.ProductID)

      setPickerProduct(detailProduct)
      setPickerOpen(true)
    } catch (error) {
      console.error(error)
      showToast('取得商品規格失敗，請稍後再試', 'error', 2600)
    } finally {
      setAddingId(null)
    }
  }

  return (
    <>
      <div className={`col-6 col-md-${mdLength} col-lg-${lgLength}`}>
        <div className={styles.productCard}>
          {/* 圖片區 */}
          <div className={styles.imageArea}>
            <ProductSwiper
              product={elm}
              productUrl={productUrl}
              navKey={`home-${elm.ProductID}-${i}`}
              priority={i < 4}
              imageAspectType={imageAspectType}
            />

            {storeInfo?.CartButtonEnable && cartButtonPosition === 'Image' && (
              <button
                type="button"
                className={styles.addCartBtn}
                onClick={handleAddClick}
                disabled={addingId === elm.ProductID || !canAddToCart}
              >
                {addingId === elm.ProductID
                  ? '處理中...'
                  : !canAddToCart
                    ? '補貨中'
                    : '加入購物車'}
              </button>
            )}

            {storeInfo?.WishlistButtonEnable &&
              wishlistButtonPosition === 'Image' && (
                <div className={styles.wishlistBox}>
                  <AddToWishlistButton
                    Product={elm}
                    type={wishType}
                    divObj={divObj}
                    WishlistButtonPosition="Image"
                  />
                </div>
              )}
          </div>

          {/* 商品資訊區 */}
          <div className={styles.infoArea}>
            <div className={styles.titleBox}>
              <h6
                className={styles.productTitle}
                style={{ fontSize: `${fontSize}px` }}
              >
                <span>
                  {elm.ProductName}
                  {isPreorder && ' (預購)'}
                </span>
              </h6>
            </div>

            <div className={styles.bottomArea}>
              <div
                className={styles.price}
                style={{ fontSize: `${priceFontSize}px` }}
              >
                {hasDiscount ? (
                  <div className="d-flex flex-column">
                    <span className={styles.oldPrice}>
                      NT$ {Number(elm.SuggestPrice).toLocaleString()}
                    </span>
                    <span className={styles.salePrice}>
                      NT$ {Number(elm.Price).toLocaleString()}
                    </span>
                  </div>
                ) : (
                  <span className={styles.normalPrice}>
                    NT${' '}
                    {Number(
                      elm.SuggestPrice ?? elm.Price ?? 0
                    ).toLocaleString()}
                  </span>
                )}
              </div>

              {(wishlistButtonPosition === 'price' ||
                cartButtonPosition === 'price') && (
                <div
                  className={styles.priceActionBox}
                  style={{ fontSize: `${priceFontSize}px` }}
                >
                  {storeInfo?.CartButtonEnable &&
                    cartButtonPosition === 'price' &&
                    (!canAddToCart ? (
                      <span className={styles.priceSoldOutText}>補貨中</span>
                    ) : (
                      <button
                        type="button"
                        className={styles.priceCartBtn}
                        onClick={handleAddClick}
                        disabled={addingId === elm.ProductID}
                        title={
                          addingId === elm.ProductID
                            ? '處理中...'
                            : '加入購物車'
                        }
                      >
                        {addingId === elm.ProductID ? (
                          <span className={styles.cartLoadingText}>...</span>
                        ) : (
                          <svg
                            className={styles.cartIcon}
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M6.2 6.5h15.1l-1.6 8.2H8L6.2 6.5Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6.2 6.5L5.4 3H3"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                            <circle
                              cx="9.2"
                              cy="20"
                              r="1.4"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <circle
                              cx="18.2"
                              cy="20"
                              r="1.4"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                        )}
                      </button>
                    ))}
                  {storeInfo?.WishlistButtonEnable &&
                    wishlistButtonPosition === 'price' && (
                      <AddToWishlistButton
                        Product={elm}
                        type={wishType}
                        divObj={divObj}
                        WishlistButtonPosition="Price"
                      />
                    )}
                </div>
              )}
            </div>

            {/* <div className={styles.divider} /> */}

            {/* <div className="d-flex justify-content-end">
              <Link
                href={productUrl}
                className={styles.details}
                style={{ fontSize: `${fontSize}px` }}
              >
                DETAILS <span>→</span>
              </Link>
            </div> */}
          </div>
        </div>
      </div>

      {/* 規格挑選彈窗 */}
      {pickerOpen && (
        <div
          onClick={() => setPickerOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.5)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 820,
              maxHeight: '85vh',
              overflow: 'auto',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,.25)',
              padding: 16,
              position: 'relative',
            }}
          >
            <button
              aria-label="關閉"
              onClick={() => setPickerOpen(false)}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 36,
                height: 36,
                border: '1px solid #e5e5e5',
                borderRadius: 8,
                background: '#fff',
                cursor: 'pointer',
                fontSize: 20,
                lineHeight: '32px',
                zIndex: 4,
              }}
              title="關閉"
            >
              ×
            </button>

            {pickerProduct && (
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  alignItems: 'center',
                  paddingRight: 56,
                  marginBottom: 8,
                  position: 'relative',
                  zIndex: 3,
                  background: '#fff',
                }}
              >
                <Image
                  unoptimized
                  src={
                    pickerProduct?.PictureData?.[0]?.Name ||
                    pickerProduct?.PictureData?.[0]?.PicturePath ||
                    '/images/placeholder/placeholder-600.png'
                  }
                  alt={pickerProduct?.ProductName || 'product'}
                  width={96}
                  height={96}
                  style={{ borderRadius: 8, objectFit: 'cover' }}
                />

                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 16,
                      lineHeight: 1.35,
                      marginBottom: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                    title={`${pickerProduct?.ProductName || ''}${pickerProduct?.IsPreorder ? ' (預購)' : ''}`}
                  >
                    {pickerProduct?.ProductName}
                    {pickerProduct?.IsPreorder && ' (預購)'}
                  </div>

                  {priceNode(
                    pickerSkuPrice ?? pickerProduct?.Price ?? 0,
                    pickerSkuOldPrice ?? pickerProduct?.SuggestPrice ?? 0
                  )}
                </div>
              </div>
            )}

            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 96,
                background: '#fff',
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <OptionPicker
                open={pickerOpen}
                product={pickerProduct}
                cardProduct={cartProducts}
                isPreorder={pickerProduct?.IsPreorder === true}
                onClose={() => setPickerOpen(false)}
                onSkuChange={(sku, unitPrice, oldPrice) => {
                  setPickerSkuObj(sku || null)
                  setPickerSkuPrice(toNumber(unitPrice))
                  setPickerSkuOldPrice(toNumber(oldPrice))
                }}
                onConfirm={async (
                  skuId,
                  qty,
                  limitQty,
                  invQty,
                  unitPrice,
                  chosenSku
                ) => {
                  if (!pickerProduct) return

                  try {
                    const limit = Number(limitQty)
                    const inv = Number(invQty)
                    const isPreorderProduct = pickerProduct?.IsPreorder === true

                    if (Number.isFinite(limit) && limit > 0 && qty > limit) {
                      alert(`\n超過此商品購買上限: ${limit}，故無法加入購物車`)
                      return
                    }

                    // 預購商品不判斷庫存
                    if (
                      !isPreorderProduct &&
                      Number.isFinite(inv) &&
                      inv >= 0 &&
                      qty > inv
                    ) {
                      alert(`\n超過此商品庫存，故無法加入購物車`)
                      return
                    }

                    setAddingId(pickerProduct.ProductID)

                    const skuObj =
                      chosenSku ||
                      (pickerProduct?.ProductOption || []).find(
                        (s) => s.SkuID === skuId
                      ) ||
                      null

                    addProductToCart({
                      ProductID: pickerProduct.ProductID,
                      updated: {
                        ...normalizeForCartWithSku(
                          pickerProduct,
                          skuObj,
                          unitPrice
                        ),
                        Qty: Number(qty) || 1,
                        Quantity: Number(qty) || 1,
                      },
                    })

                    setPickerOpen(false)
                    // showToast('成功加入購物車', 'success')
                    fetchCartListOnce()
                  } catch (e) {
                    console.error(e)
                    // showToast('加入購物車失敗，請稍後再試', 'error', 2600)
                  } finally {
                    setAddingId(null)
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2000,
            background:
              toast.type === 'success'
                ? 'rgba(25,135,84,.95)'
                : 'rgba(220,53,69,.95)',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.18)',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: '.3px',
          }}
        >
          {toast.msg}
        </div>
      )}
    </>
  )
}
